import { Fragment, useEffect, useCallback, useState } from "react";
import { TableRowLoader } from "components/loaders";
import { sortOptions } from "content/SortDropdown";
import * as Components from "components";
import { debounce, downloadBlob, formatThousand } from "utils";
import useFetch from "hooks/useFetch";
import { toast } from "react-toastify";
import apiClient from "config/api";
import { useLocation } from "react-router";
import {
  RevenueDefaultFilters,
  RevenueDefaultMultiFilter,
} from "content/DefaultFilter";
import { RevenueTableColumns } from "content/TableColumns";
import { RevenueReportDropdownOptions } from "content/Select";

const RevenueReports = () => {
  const [checkedTableMenuColumn, setCheckedTableMenuColumn] =
    useState(RevenueTableColumns);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [filter, setFilter] = useState(RevenueDefaultFilters);
  const [search, setSearch] = useState("");
  const [isRerender, setIsRerender] = useState(false);
  const [exportValue, setExportValue] = useState("excel");
  const { data, loading } = useFetch("/admin/reports/revenue-reports", filter, [
    filter,
    isRerender,
  ]);
  const location = useLocation();
  const { startDate, endDate } = location.state || {
    startDate: null,
    endDate: null,
  };

  useEffect(() => {
    if (startDate && endDate) {
      setFilter((prev) => ({
        ...prev,
        fromDate: startDate,
        toDate: endDate,
        page: 1,
      }));
      setIsRerender((prev) => !prev);
    }
  }, [startDate, endDate]);

  // Memoize the debouncedSearch function
  const debouncedSearch = useCallback(
    debounce((value) => {
      handleFilter("search", value);
    }, 500),
    []
  );

  // Handle search change
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearch(value);
    debouncedSearch(value);
  };

  // Handle filter updates
  const handleFilter = (name, value) => {
    if (name === "filter") {
      setFilter((prev) => ({
        ...prev,
        ...value,
        page: 1,
      }));
    } else if (name === "pagination") {
      setFilter((prev) => ({
        ...prev,
        page: value.current,
        limit: value.pageSize,
      }));
    } else {
      setFilter((prev) => ({
        ...prev,
        [name]: value,
        page: 1,
      }));
    }
    setSelectedRowKeys([]);
  };

  // Handle multi-filter changes
  const handleFilterChange = (selectedFilters) => {
    const combinedFilters = Object.values(selectedFilters).flat();

    const formattedFilters = combinedFilters.reduce(
      (acc, item) => {
        if (item.fromDate || item.toDate) {
          acc.fromDate = item.fromDate;
          acc.toDate = item.toDate;
        }
        return acc;
      },
      { fromDate: "", toDate: "" }
    );

    handleFilter("filter", formattedFilters);
  };

  // Handle export selection
  const handleSelectChange = async (value) => {
    setExportValue(value);
  };

  // Handle export to Excel or PDF
  const handleExport = async () => {
    const endpoint =
      exportValue === "excel"
        ? "/admin/reports/revenue-reports/export-excel"
        : "/admin/reports/revenue-reports/export-pdf";
    const fileExtension = exportValue === "excel" ? "xlsx" : "pdf";
    const mimeType =
      exportValue === "excel"
        ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        : "application/pdf";

    const toastId = toast.loading(`Preparing ${exportValue} export...`);
    try {
      const response = await apiClient.get(endpoint, {
        params: filter,
        responseType: "arraybuffer",
      });
      if (response?.data) {
        toast.update(toastId, {
          render: `${exportValue.toUpperCase()} export started successfully!`,
          type: "success",
          isLoading: false,
          autoClose: 3000,
        });

        const blob = new Blob([response.data], { type: mimeType });
        downloadBlob(blob, `revenue-report.${fileExtension}`);
      } else {
        toast.error("No data received from the API.");
      }
    } catch (error) {
      toast.update(toastId, {
        render: error.message || "Failed to export data",
        type: "error",
        isLoading: false,
        autoClose: 3000,
      });
    }
  };

  // Handle row selection
  const onRowSelectionChange = (selectedKeys) => {
    setSelectedRowKeys(selectedKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onRowSelectionChange,
  };

  // Table columns
  const columns = [
    {
      title: "MONTH",
      dataIndex: "month",
      loader: <TableRowLoader />,
      render: (_, record) => `${record.month} ${record.year}`,
    },
    {
      title: "SUBSCRIPTION REVENUE",
      dataIndex: "subscription",
      loader: <TableRowLoader />,
      render: (_, record) => `$${formatThousand(record.subscription)}`,
    },
    {
      title: "INVOICE REVENUE",
      dataIndex: "invoice",
      loader: <TableRowLoader />,
      render: (_, record) => `$${formatThousand(record.invoice)}`,
    },
    {
      title: "ADD-ONS REVENUE",
      dataIndex: "addon",
      loader: <TableRowLoader />,
      render: (_, record) => `$${formatThousand(record.addon)}`,
    },
    {
      title: "Total Stripe Charges",
      dataIndex: "addon",
      loader: <TableRowLoader />,
      render: (_, record) => `$${formatThousand(record.stripeChargesTotal)}`,
    },
    {
      title: "TOTAL REVENUE With Charges",
      dataIndex: "total",
      loader: <TableRowLoader />,
      render: (_, record) =>
        `$${formatThousand(record.total + record.stripeChargesTotal)}`,
    },
  ];

  return (
    <Fragment>
      <div className="flex flex-col border dark:border-gray-800 my-5 2xl:my-10 mx-5 2xl:mx-14 rounded-lg shadow-lg dark:bg-[#181818]">
        <div className="w-full flex items-center px-5 py-3">
          <div className="w-full flex gap-5">
            <div className="flex gap-3 items-center w-full xl:w-3/5">
              <div id="revenue-search" className="w-full xl:w-[400px]">
                <Components.SearchField
                  mobilePlaceholder="Search"
                  placeholder="Search by Month or Year"
                  value={search}
                  onChange={handleSearchChange}
                />
              </div>
              <Components.SortDropdown
                sortOptions={sortOptions}
                propertyType={true}
                onSortChange={(value) => handleFilter("sortBy", value)}
              />
              <Components.MultiCategoryFilter
                categories={RevenueDefaultMultiFilter}
                onFilterChange={handleFilterChange}
              />
            </div>
            <div className="flex items-center justify-end gap-4 xl:w-1/2">
              <div className="basis-[300px]">
                <Components.Select
                  options={RevenueReportDropdownOptions}
                  defaultValue="excel"
                  onChange={handleSelectChange}
                />
              </div>
              <button
                className="text-[1em] rounded-md h-10 flex items-center justify-center px-5 lg:px-3 xl:px-5 font-medium gap-3 lg:gap-1 xl:gap-3 transition-all duration-300 border border-secondary text-secondary"
                onClick={handleExport}
              >
                Export
              </button>
              <Components.TableCustomizeColumnMenu
                setCheckedTableMenuColumn={setCheckedTableMenuColumn}
                checkedTableMenuColumn={checkedTableMenuColumn}
              />
            </div>
          </div>
        </div>
        <div id="revenue-table">
          <Components.CustomTable
            columns={columns.filter((column) =>
              checkedTableMenuColumn.some(
                (col) => col.dataIndex === column.dataIndex && col.visible
              )
            )}
            dataSource={data?.data}
            pagination={true}
            // rowSelection={rowSelection}
            isLoading={loading}
            metaData={data?.metaData}
            setPagination={(pagination) =>
              handleFilter("pagination", pagination)
            }
            pageLimit={filter.limit}
          />
        </div>
      </div>
    </Fragment>
  );
};

export default RevenueReports;
